/**
 * Supabase Client Configuration
 * Database client for Product Asset Scheduler
 * 
 * Multi-tenant setup: Each request includes shop domain header for RLS
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { getSessionTokenForBackend } from './authenticated-fetch';

function getRequiredEnv(name: string): string {
  const v = (typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env[name]) as string | undefined;
  if (!v) {
    throw new Error(`[Config] Missing required env var: ${name}`);
  }
  return v;
}

// Required in all environments; do not bake a project URL or key into the bundle.
const SUPABASE_URL = getRequiredEnv('VITE_SUPABASE_URL');
const SUPABASE_ANON_KEY = getRequiredEnv('VITE_SUPABASE_ANON_KEY');

// Store the current shop domain for multi-tenant isolation
let currentShopDomain: string = '';

/**
 * Get shop domain from various sources (URL params, env, etc.)
 */
function resolveShopDomain(): string {
  // Check cached value first
  if (currentShopDomain) return currentShopDomain;

  // For Shopify embedded apps, shop domain comes from the URL / Shopify global
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const shopFromUrl = urlParams.get('shop');
    if (shopFromUrl) {
      currentShopDomain = shopFromUrl;
      return currentShopDomain;
    }
    
    // Check Shopify global
    const shopifyGlobal = (window as any).shopify;
    if (shopifyGlobal?.config?.shop) {
      currentShopDomain = shopifyGlobal.config.shop;
      return currentShopDomain;
    }
  }

  // Check environment variable (fallback for local dev / scripts)
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SHOPIFY_SHOP_DOMAIN) {
    currentShopDomain = import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN;
    return currentShopDomain;
  }
  
  return '';
}

/**
 * Set the shop domain (call this when App Bridge initializes)
 */
export function setShopDomain(domain: string) {
  currentShopDomain = domain;
  // Recreate client only if this is the first time or domain changed
  // This ensures RLS headers are set correctly
  supabaseInstance = createSupabaseClient(domain);
  
  // No-op: env vars are required and validated at module init.
}

/**
 * Get the current shop domain
 */
export function getShopDomain(): string {
  return currentShopDomain || resolveShopDomain();
}

/**
 * Create a Supabase client with shop domain header for RLS
 */
function createSupabaseClient(shopDomain?: string): SupabaseClient {
  const domain = shopDomain || resolveShopDomain();
  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        'x-shop-domain': domain,
      },
    },
  });
}

// Create initial client
let supabaseInstance = createSupabaseClient();

// Export a getter that always returns the current instance
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    return (supabaseInstance as any)[prop];
  },
});

// Also export direct access for cases where proxy doesn't work
export function getSupabaseClient(): SupabaseClient {
  return supabaseInstance;
}

function formatScheduleProcessorError(result: any): string | undefined {
  if (!result || typeof result !== 'object') return undefined;
  if (typeof result.error === 'string' && result.error.trim()) return result.error.trim();

  const results = Array.isArray(result.results) ? result.results : [];
  const firstFailure = results.find((entry: any) => entry && entry.success === false);
  if (!firstFailure) return undefined;

  if (typeof firstFailure.error === 'string' && firstFailure.error.trim()) {
    return firstFailure.error.trim();
  }

  const themeFailures = Array.isArray(firstFailure.themeResults)
    ? firstFailure.themeResults.filter((themeResult: any) => themeResult?.status === 'failed')
    : [];

  if (themeFailures.length > 0) {
    return themeFailures
      .map((themeResult: any) => {
        const themeName = typeof themeResult.themeName === 'string' && themeResult.themeName.trim()
          ? themeResult.themeName.trim()
          : 'Theme';
        const error = typeof themeResult.error === 'string' && themeResult.error.trim()
          ? themeResult.error.trim()
          : 'Unknown error';
        return `${themeName}: ${error}`;
      })
      .join('; ');
  }

  return undefined;
}

async function callProcessSchedules(body: Record<string, any>): Promise<{ data?: any; error?: string; status: number }> {
  const shopDomain = getShopDomain();
  const sessionToken = await getSessionTokenForBackend();
  const endpoint = `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/process-schedules`;

  // Browser calls should NOT include Authorization header - that makes the function think
  // it's an internal cron call. Instead, we use sessionToken for authentication.
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      ...(shopDomain ? { 'x-shop-domain': shopDomain } : {}),
      ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
    },
    body: JSON.stringify(body),
  });

  const responseText = await response.text();
  let data: any = null;
  try {
    data = responseText ? JSON.parse(responseText) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    const errorMessage = (typeof data?.error === 'string' && data.error.trim())
      ? data.error.trim()
      : (responseText || `HTTP ${response.status}`);
    return { error: errorMessage, status: response.status, data };
  }

  return { data, status: response.status };
}

/**
 * Trigger the schedule processor Edge Function to run immediately.
 * Use this for "Execute Now" functionality instead of waiting for cron.
 */
export async function triggerScheduleProcessor(): Promise<{ success: boolean; processed?: number; error?: string }> {
  try {
    const shopDomain = getShopDomain();
    const { data, error } = await callProcessSchedules({ ...(shopDomain ? { shopDomain } : {}) });

    if (error) throw new Error(error);

    const processed = Number(data?.processed || 0);
    const successCount = Number(data?.successCount || 0);
    const failureCount = Number(data?.failureCount || 0);
    const reportedSuccess = Boolean(data?.success);
    const detailedError = formatScheduleProcessorError(data);

    // Treat a run with failures as unsuccessful for UI purposes.
    const ok = reportedSuccess && failureCount === 0;
    const errMsg = detailedError || data?.error || (failureCount > 0
      ? `${failureCount} job(s) failed${processed ? ` (processed ${processed})` : ''}`
      : undefined);

    return {
      success: ok,
      processed,
      error: errMsg,
      // keep extra fields for callers that inspect them
      ...(typeof data?.successCount !== 'undefined' ? { successCount } : {}),
      ...(typeof data?.failureCount !== 'undefined' ? { failureCount } : {}),
      ...(data?.results ? { results: data.results } : {}),
    } as any;
  } catch (error) {
    console.error('[triggerScheduleProcessor] Failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Send a test email notification using the configured Resend integration.
 *
 * This uses the `process-schedules` Edge Function's `testEmail` mode, which
 * sends to all notification recipients configured for the current shop.
 */
export async function sendTestEmailNotifications(): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  recipients?: string[];
  status?: number;
  rawBody?: string;
}> {
  try {
    const shopDomain = getShopDomain();
    const { data, error, status } = await callProcessSchedules({ testEmail: true, ...(shopDomain ? { shopDomain } : {}) });
    if (error) {
      return { success: false, error, status, rawBody: error };
    }

    const success = !!data?.success;
    const message = data?.message;
    const errMsg = data?.error;

    if (!success) {
      console.warn('[sendTestEmailNotifications] Test email reported failure', { data });
    }

    return {
      success,
      message,
      error: !success ? (errMsg || message) : errMsg,
      recipients: data?.recipients,
      status: 200,
      rawBody: JSON.stringify(data),
    };
  } catch (error) {
    console.error('[sendTestEmailNotifications] Failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// Re-export for convenience
export default supabase;
